
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action } from 'vuex-class';
import Toast from '@/utils/toast';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import SuitesPanel from '@/components/shared/suites/panels/SuitesPanel.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import WarningModal from '../shared/suites/WarningModal.vue';

@Component<OrderModificationRequestModal>({
	components: {
		InfoBanner,
		StandardModal,
		SuitesPanel,
		TextButton,
		WarningModal
	}
})
export default class OrderModificationRequestModal extends Vue {
	@Action('fetchOrderModificationRequests', { namespace: 'orders' }) private fetchOrderModificationRequests!: (orderId: string) => Promise<OrderModificationRequest[]>;
	@Action('updateOrderModificationRequest', { namespace: 'orders' }) private updateOrderModificationRequest!: (payload: OrderModificationRequest) => Promise<void>;
	@Action('setActiveModificationRequest', { namespace: 'orders' }) private setActiveModificationRequest!: (modificationRequest: OrderModificationRequest) => void;
	@Prop({ type: Boolean, required: true, default: false }) private showModal!: boolean;
	@Prop({ type: String, required: true }) private orderId!: string;

	private loading: Record<string, boolean> = {
		accepted: false,
		declined: false,
		modal: false
	};
	private warningModalOpen: boolean = false;
	private modificationRequests: OrderModificationRequest[] = [];

	/**
	 * Get active (pending) modification request
	 *
	 * @return {OrderModificationRequest | undefined}
	 */
	private get activeRequest(): OrderModificationRequest | undefined {
		return this.modificationRequests.find(request => request.status === 'pending');
	}

	/**
	 * Get previous modification requests (not pending)
	 *
	 * @return {OrderModificationRequest[]}
	 */
	private get previousRequests(): OrderModificationRequest[] {
		return this.modificationRequests.filter(request => request.status !== 'pending');
	}

	/**
	 * Fetch modification requests for the order
	 *
	 * @return {Promise<void>}
	 */
	private async created() {
		try {
			this.loading.modal = true;
			this.modificationRequests = await this.fetchOrderModificationRequests(this.orderId);
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.management.modification_request_modal.error_fetching_requests', { errorMessage }));
		}
		finally {
			this.loading.modal = false;
		}
	}

	/**
	 * Update modification request status (accept or decline)
	 *
	 * @param {OrderModificationStatus} action
	 * @return {Promise<void>}
	 */
	private async declineRequest(): Promise<void> {
		if (!this.activeRequest) {
			return;
		}

		this.loading.declined = true;
		try {
			await this.updateOrderModificationRequest({
				...this.activeRequest,
				status: 'declined'
			});
			this.modificationRequests = await this.fetchOrderModificationRequests(this.orderId);
			this.$emit('request-updated', this.modificationRequests);
			this.warningModalOpen = false;
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.management.modification_request_modal.error_updating_request', { errorMessage }));
		}
		finally {
			this.loading.declined = false;
		}
	}

	private setRequestAndModifyOrder(): void {
		if (this.activeRequest) {
			this.setActiveModificationRequest(this.activeRequest);
			this.$router.push(`/order-management/${this.activeRequest.order_id}?modificationRequest=${this.activeRequest.id}`).catch(() => {});
		}
	}
}
